import React, { useCallback, useContext, useEffect, useRef } from "react"
import { useEventListener } from "ahooks"
import anime from "animejs"
import classNames from "classnames"
import { clamp, round } from "lodash-es"
import { usePresence } from "motion/react"
import { useLandscapeDisplayInfo } from "@hg/layouts"

import { useScale } from "@/hooks"
import { useETL, useTranslation } from "@/locales"
import VideoPlayer from "@/topic/rogue_5/components/VideoPlayer"
import { GlobalContext } from "@/topic/rogue_5/context"
import { isMobile, sleep } from "@/utils/misc"
import LobbyButton from "../../components/LobbyButton"
import { EGameScene, useGameStore, useShowUI } from "../../store"

import bg from "../../../../assets/img/game/lobby/first-frame.png"
import VideoGame from "../../../../assets/video/game.mp4"
import styles from "./index.module.less"

const basic_front_in_animation = {
    opacity: [0, 1],
    filter: ["blur(10px)", "blur(0px)"],
    duration: 1000,
    easing: "easeOutCubic",
}

function getMountainAnimate(left: boolean) {
    return {
        // translateX: [left ? "15vw" : "-15vw", 0],
        ...basic_front_in_animation,
    }
}

const SCENE_ANIMATION = {
    animate: async (ele: HTMLDivElement) => {
        // const mountainLeft = ele.querySelector(`.${styles.mountainLeft}`)
        // const mountainRight = ele.querySelector(`.${styles.mountainRight}`)
        // const main = ele.querySelector(`.${styles.main}`)
        // const fence = ele.querySelector(`.${styles.fence}`)
        // const sun = ele.querySelector(`.${styles.sun}`)
        const sceneBg = ele.querySelector(`.${styles.sceneBg}`)
        const leftButton = ele.querySelector(
            `.${styles.leftButton}`
        ) as HTMLDivElement
        const rightButton = ele.querySelector(
            `.${styles.rightButton}`
        ) as HTMLDivElement

        const timeline = anime.timeline()
        // timeline.add(
        //     {
        //         targets: mountainLeft,
        //         ...getMountainAnimate(true),
        //         translateX: ["-3rem", "0"],
        //     },
        //     0
        // )
        // timeline.add(
        //     {
        //         targets: mountainRight,
        //         ...getMountainAnimate(false),
        //         translateX: ["3rem", "0"],
        //     },
        //     0
        // )
        // timeline.add(
        //     {
        //         targets: sun,
        //         ...basic_front_in_animation,
        //     },
        //     0
        // )
        timeline.add(
            {
                targets: sceneBg,
                ...basic_front_in_animation,
                scale: [0.8, 1],
                duration: 2000,
            },
            0
        )
        // timeline.add(
        //     {
        //         targets: fence,
        //         ...basic_front_in_animation,
        //     },
        //     1400
        // )
        leftButton &&
            timeline.add(
                {
                    targets: leftButton,
                    ...basic_front_in_animation,
                    translateY: ["-3rem", "0"],
                    duration: 1500,
                    complete: () => {
                        if (leftButton) leftButton.style.pointerEvents = "auto"
                    },
                },
                1600
            )
        timeline.add(
            {
                targets: rightButton,
                ...basic_front_in_animation,
                translateY: ["-3rem", "0"],
                duration: 1500,
                complete: () => {
                    if (rightButton) rightButton.style.pointerEvents = "auto"
                },
            },
            2100
        )
        return timeline.finished
    },
    exit: async (ele: HTMLDivElement) => {
        const main = ele.querySelector(`.${styles.main}`)
        const leftButton = ele.querySelector(`.${styles.leftButton}`)
        const rightButton = ele.querySelector(`.${styles.rightButton}`)

        const timeline = anime.timeline()
        timeline.add(
            {
                targets: [leftButton, rightButton].filter(Boolean),
                duration: 800,
                opacity: [1, 0],
                translateY: ["0", "-80%"],
                filter: ["blur(0px)", "blur(10px)"],
                easing: "easeInCubic",
            },
            0
        )
        timeline.add(
            {
                targets: ele,
                scale: [1, 1.75],
                filter: ["blur(0px)", "blur(2px)"],
                duration: 2000,
                // translateY: ["0", "25%"],
                easing: "easeInCubic",
                begin: () => {
                    ele.style.transformOrigin =
                        "calc(50% - 6rem) calc(50% - 30rem)"
                },
            },
            400
        )
        timeline.add(
            {
                targets: ele,
                opacity: [1, 0],
                duration: 1500,
                easing: "easeInCubic",
            },
            900
        )
        return timeline.finished
    },
}

const GameSceneLobby: React.FC<{
    className?: string
    style?: React.CSSProperties
}> = ({ className, style }) => {
    const [isPresent, safeToRemove] = usePresence()
    const { t } = useTranslation()
    const { setScene } = useGameStore()
    const containerRef = useRef<HTMLDivElement>(null)
    const showUI = useShowUI()
    useEffect(() => {
        if (!containerRef.current) return
        if (isPresent) {
            SCENE_ANIMATION.animate(containerRef.current)
            showUI(true)
        } else {
            SCENE_ANIMATION.exit(containerRef.current)
                .then(() => sleep(200))
                .then(() => {
                    // console.log("exit")
                    safeToRemove()
                })
        }
    }, [isPresent])

    const {
        state: { fontSize },
    } = useContext(GlobalContext)
    const { inRotate, width, height } = useLandscapeDisplayInfo()
    const scale = useScale(fontSize)
    const scene = useRef<HTMLDivElement>(null)
    const animationId = useRef(0)
    const fromPoint = useRef({ x: 0, y: 0 })
    const toPoint = useRef({ x: 0, y: 0 })
    const currentPoint = useRef({ x: 0, y: 0 })
    const lastTouchPoint = useRef({ x: 0, y: 0 })

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!scene.current || isMobile()) {
                return
            }

            const { clientX, clientY } = e
            const x = inRotate ? clientY : clientX
            const y = inRotate ? height - clientX : clientY

            const { clientWidth, clientHeight } = scene.current

            fromPoint.current = currentPoint.current
            toPoint.current = {
                x:
                    (x - width / 2) *
                    (((clientWidth * scale - width) * 0.5) / (width * 0.5)) *
                    -1,
                y:
                    (y - height / 2) *
                    (((clientHeight * scale - height) * 0.5) / (height * 0.5)) *
                    -1,
            }
        },
        [height, inRotate, scale, width]
    )

    const handleTouchStart = useCallback(
        (e: TouchEvent) => {
            const { clientX, clientY } = e.touches[0]
            if (inRotate) {
                lastTouchPoint.current = { x: clientY, y: height - clientX }
            } else {
                lastTouchPoint.current = { x: clientX, y: clientY }
            }
        },
        [height, inRotate]
    )

    const handleTouchMove = useCallback(
        (e: TouchEvent) => {
            if (!scene.current) {
                return
            }

            const { clientWidth, clientHeight } = scene.current

            const paddingX = (clientWidth * scale - width) * 0.5
            const paddingY = (clientHeight * scale - height) * 0.5

            let { clientX, clientY } = e.touches[0]
            if (inRotate) {
                clientX = e.touches[0].clientY
                clientY = height - e.touches[0].clientX
            }

            const dx = clientX - lastTouchPoint.current.x
            const dy = clientY - lastTouchPoint.current.y
            lastTouchPoint.current = { x: clientX, y: clientY }

            fromPoint.current = currentPoint.current
            toPoint.current = {
                x: clamp(
                    toPoint.current.x + dx * (paddingX / (width * 0.5)),
                    -paddingX,
                    paddingX
                ),
                y: clamp(
                    toPoint.current.y + dy * (paddingY / (height * 0.5)),
                    -paddingY,
                    paddingY
                ),
            }
        },
        [height, inRotate, scale, width]
    )

    useEventListener("mousemove", handleMouseMove, { target: window })
    useEventListener("touchstart", handleTouchStart, { target: window })
    useEventListener("touchmove", handleTouchMove, { target: window })

    useEventListener(
        "resize",
        () => {
            if (scene.current) {
                fromPoint.current = { x: 0, y: 0 }
                toPoint.current = { x: 0, y: 0 }
                currentPoint.current = { x: 0, y: 0 }
                lastTouchPoint.current = { x: 0, y: 0 }
                scene.current.style.transform = `scale(${scale})`
            }
        },
        { target: window }
    )

    const moveStep = useCallback(() => {
        const point = (currentPoint.current = {
            x:
                currentPoint.current.x +
                (toPoint.current.x - currentPoint.current.x) * 0.1,
            y:
                currentPoint.current.y +
                (toPoint.current.y - currentPoint.current.y) * 0.1,
        })

        if (scene.current) {
            const transform = `scale(${scale}) translate3d(${round(point.x / scale, 2)}px, ${round(
                point.y / scale,
                2
            )}px, 0)`

            if (scene.current.style.transform !== transform) {
                scene.current.style.transform = transform
            }
        }

        animationId.current = window.requestAnimationFrame(() => {
            moveStep()
        })
    }, [scale])

    useEffect(() => {
        animationId.current = window.requestAnimationFrame(() => {
            moveStep()
        })

        return () => {
            window.cancelAnimationFrame(animationId.current)
        }
    }, [moveStep])
    const collect = useETL()
    return (
        <div
            className={classNames(styles.sceneContainer, className)}
            style={style}
            ref={containerRef}
        >
            <div className={styles.sceneBg}>
                {/* <div className={styles.mountainLeft}></div>
                <div className={styles.mountainRight}></div>
                <div className={styles.fence}></div> */}

                {/* <div className={styles.sun}></div> */}
                <div
                    className={styles.main}
                    ref={scene}
                    style={{ transform: `scale(${scale})` }}
                >
                    <VideoPlayer
                        className={styles.video}
                        src={VideoGame}
                        poster={bg}
                    />

                    {/* <div className={styles.statue}></div>
                    <div className={styles.water}></div> */}
                    <LobbyButton
                        className={styles.leftButton}
                        type="blue"
                        text={t("game.lobby.button.1")}
                        lock={__ROGUE_5_PRE}
                        textEn="ASK FOR/nFORTUNE"
                        onClick={() => {
                            if (__ROGUE_5_PRE) return
                            collect("button_click", {
                                button_name: "game_start_rhodes",
                            })
                            useGameStore.setState({
                                gameType: "rhodes",
                            })
                            setScene(EGameScene.Game)
                        }}
                    />
                    <LobbyButton
                        className={styles.rightButton}
                        type="pink"
                        text={t("game.lobby.button.2")}
                        textEn="ASK FOR/nFORTUNE"
                        onClick={() => {
                            collect("button_click", {
                                button_name: "game_start_normal",
                            })
                            useGameStore.setState({
                                gameType: "normal",
                            })
                            setScene(EGameScene.Game)
                        }}
                    />
                </div>
            </div>
        </div>
    )
}

export default GameSceneLobby
